import { TileLayer, WMSTileLayer } from 'react-leaflet';
import { useMemo } from 'react';
import { MapProviderType } from '@customer/store/apis/authApi';
import useMapConfig from '../hooks/useMapConfig';

interface TileLayerConfig {
  url: string;
  attribution?: string;
}

interface WMSTileLayerConfig extends TileLayerConfig {
  layers: string;
}

function isWMS(config: TileLayerConfig | WMSTileLayerConfig): config is WMSTileLayerConfig {
  return 'layers' in config;
}

// No map provider should ever need to zoom more than this.
const DEFAULT_URL_PATTERN = '{z}/{x}/{y}.png';

const getTileUrl = (uri: string, tilePattern: string = DEFAULT_URL_PATTERN): string => {
  const suffix = uri.endsWith('/') ? tilePattern : `/${tilePattern}`;
  return `${uri}${suffix}`;
};

const openStreetMapConfig: TileLayerConfig = {
  url: getTileUrl('https://{s}.tile.openstreetmap.org'),
  attribution:
    '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors',
};

export function TileLayers() {
  const { maxZoom, maxNativeZoom, uri, layerName, providerType } = useMapConfig();

  const tileLayerConfigs = useMemo<(WMSTileLayerConfig | TileLayerConfig)[]>(() => {
    const configs: (WMSTileLayerConfig | TileLayerConfig)[] = [];

    if (uri) {
      switch (providerType) {
        case MapProviderType.ArcGIS:
          configs.push({ url: getTileUrl(uri, 'tile/{z}/{y}/{x}') });
          break;

        case MapProviderType.GeoServer: {
          if (layerName) {
            configs.push({ url: uri, layers: layerName });
          }
          break;
        }

        case MapProviderType.RasterOnly:
          configs.push({ url: getTileUrl(uri) });
          break;

        case MapProviderType.RasterWithOpenStreetMap:
          configs.push(openStreetMapConfig);
          configs.push({ url: getTileUrl(uri) });
          break;

        case MapProviderType.OpenStreetMap:
        default:
          break;
      }
    }

    // Use OpenStreetMap as the tile layer if no tile layers have been configured yet.
    if (!configs.length) {
      configs.push(openStreetMapConfig);
    }

    return configs;
  }, [providerType, uri, layerName]);

  return (
    <>
      {tileLayerConfigs.map((config: WMSTileLayerConfig | TileLayerConfig, i: number) => {
        const props = {
          ...config,
          maxZoom,
          maxNativeZoom,
          noWrap: true,
        };

        return isWMS(config) ? (
          <WMSTileLayer key={i} {...props} />
        ) : (
          <TileLayer key={i} {...props} />
        );
      })}
    </>
  );
}

export default TileLayers;
